import { useEffect, useState } from 'react';
import { centavosPiso, kmFaturavelDe, valorAoAbrirOPasso } from './valor';
import type { TarifaResposta, ParametrosWizard } from './types';
import { Selo } from '../../../common/interface';
import { moeda } from '../../formato';

interface Props {
  valorCentavos: number;
  kmIda: number;
  kmVolta: number;
  somenteIda: boolean;
  tarifa: TarifaResposta | null;
  parametros: ParametrosWizard | null;
  onChange: (centavos: number) => void;
}

/**
 * Passo 7 — handoff §2: input grande, "Piso da tarifa", chip "Acompanha a
 * sugestão" → "Valor editado" após a primeira edição manual. Piso comparado
 * na precisão que o campo exibe (centavos), nunca na base double crua.
 */
export function PassoValor({ valorCentavos, kmIda, kmVolta, somenteIda, tarifa, parametros, onChange }: Props) {
  const [tocado, setTocado] = useState(false);
  const kmFaturavel = kmFaturavelDe({
    kmIda, kmVolta, somenteIda, kmFiscalIncluiVolta: parametros?.kmFiscalIncluiVolta ?? true,
  });
  const sugestaoReais = tarifa ? Math.max(tarifa.valorMinimo, kmFaturavel * tarifa.valorPorKm) : 0;
  const pisoCentavos = centavosPiso(sugestaoReais);

  useEffect(() => {
    if (!tarifa) return;
    const aberto = valorAoAbrirOPasso({ atual: valorCentavos, sugestao: Math.round(sugestaoReais * 100), piso: pisoCentavos });
    if (aberto !== valorCentavos) onChange(aberto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarifa, kmFaturavel]);

  const permitirReduzir = parametros?.permitirReduzirValor ?? false;
  const abaixoDoPiso = !permitirReduzir && pisoCentavos > 0 && valorCentavos < pisoCentavos;

  return (
    <div className="co-pilha">
      <label className="co-campo">
        <span className="co-campo-rotulo">Valor declarado (R$)</span>
        <input
          className="co-entrada co-entrada-grande"
          type="number" step="0.01" inputMode="decimal"
          aria-invalid={abaixoDoPiso || undefined}
          aria-describedby="co-valor-piso"
          value={valorCentavos ? (valorCentavos / 100).toFixed(2) : ''}
          onChange={e => { setTocado(true); onChange(Math.round(Number(e.target.value) * 100)); }}
        />
      </label>
      <div className="co-cabecalho">
        <span id="co-valor-piso" className="co-apoio">
          {tarifa ? `Piso da tarifa: ${moeda(pisoCentavos / 100)}` : 'Sem tarifa para sugerir o valor.'}
        </span>
        <Selo papel={tocado ? 'neutro' : 'sucesso'}>{tocado ? 'Valor editado' : 'Acompanha a sugestão'}</Selo>
      </div>
      {abaixoDoPiso && (
        <p className="co-erro" role="alert">Valor abaixo do mínimo permitido para este trajeto.</p>
      )}
    </div>
  );
}
